import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, Accessibility, AlertCircle } from "lucide-react";
import { printPolicyPage } from "@/lib/printPolicy";

const handleDownload = () => printPolicyPage({ title: "Dichiarazione Accessibilità", contentElementId: "a11y-content" });

export default function DichiarazioneAccessibilitaPage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("download") === "1") {
      const t = setTimeout(() => handleDownload(), 600);
      return () => clearTimeout(t);
    }
  }, [params]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="accessibility-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={handleDownload} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#163321]/10 flex items-center justify-center">
              <Accessibility className="w-5 h-5 text-[#163321]" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-medium text-[#163321]">Dichiarazione di Accessibilità</h1>
              <p className="text-xs text-[#4A5E51] mt-0.5">In conformità al European Accessibility Act (Direttiva UE 2019/882) e alla Legge Stanca n. 4/2004</p>
            </div>
          </div>

          <article id="a11y-content" className="prose prose-sm max-w-none text-[#4A5E51] text-sm leading-relaxed space-y-5">
            <p>
              <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong> si impegna a rendere il proprio sito web accessibile, in conformità con:
            </p>
            <ul className="list-disc pl-6">
              <li><strong>Direttiva UE 2019/882</strong> (European Accessibility Act, vigente dal 28/06/2025)</li>
              <li><strong>D.Lgs. 82/2022</strong> (attuazione italiana)</li>
              <li><strong>Legge 4/2004</strong> (Legge Stanca)</li>
              <li><strong>Norme tecniche UNI CEI EN 301 549</strong> e <strong>WCAG 2.1 livello AA</strong></li>
            </ul>

            <h2 className="text-lg font-heading text-[#163321] mt-6">Stato di conformità</h2>
            <p>
              Il sito <strong>www.colucciassicuratori.com</strong> è <strong>parzialmente conforme</strong> alle WCAG 2.1 livello AA. Di seguito dettaglio delle funzionalità implementate:
            </p>

            <h3 className="text-base font-heading text-[#163321] mt-4">Funzionalità di accessibilità attive</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Widget accessibilità</strong>: pulsante in basso a sinistra del sito che consente di regolare dimensione testo, contrasto, spaziatura, font leggibile per dislessici</li>
              <li><strong>Navigazione da tastiera</strong>: tutti gli elementi interattivi sono raggiungibili via Tab</li>
              <li><strong>Focus visibile</strong>: gli elementi attivi mostrano contorno di focus</li>
              <li><strong>Etichette ARIA</strong>: form e pulsanti sono etichettati per screen reader</li>
              <li><strong>Contrasto colori</strong>: rapporto minimo 4.5:1 per testo normale, 3:1 per testo grande</li>
              <li><strong>Responsive design</strong>: sito adattato a dispositivi mobili, tablet e desktop</li>
              <li><strong>Testi alternativi</strong> per immagini informative</li>
              <li><strong>Zoom</strong>: il contenuto è leggibile fino al 200% senza perdita di funzionalità</li>
            </ul>

            <h3 className="text-base font-heading text-[#163321] mt-4">Aree con limitazioni note</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Alcuni PDF allegati (Moduli Unici Precontrattuali, Privacy Policy stampabile) potrebbero non essere pienamente accessibili a screen reader: disponibile su richiesta una versione in formato HTML</li>
              <li>Embed di Google Maps per le sedi potrebbe presentare problemi di accessibilità (componente di terze parti): gli indirizzi sono comunque forniti in formato testo leggibile</li>
            </ul>

            <h2 className="text-lg font-heading text-[#163321] mt-6">Feedback e contatti</h2>
            <div className="bg-[#F5F4F0] rounded-xl p-4 border-l-4 border-[#163321]">
              <p className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-[#163321] mt-0.5 flex-shrink-0" />
                <span>Se incontri ostacoli di accessibilità o hai suggerimenti per migliorare l'usabilità, scrivi a:</span>
              </p>
              <p><strong>Email</strong>: <a href="mailto:accessibilita@colucciassicuratori.com" className="text-[#163321] underline">accessibilita@colucciassicuratori.com</a></p>
              <p><strong>Telefono</strong>: <a href="tel:0817611000" className="text-[#163321] underline">081 761 1000</a></p>
              <p>Risposta garantita entro 15 giorni.</p>
            </div>

            <h2 className="text-lg font-heading text-[#163321] mt-6">Procedura di reclamo</h2>
            <p>
              Se la risposta ai tuoi rilievi non è soddisfacente, puoi rivolgerti all'<strong>Agenzia per l'Italia Digitale (AgID)</strong>:
            </p>
            <ul className="list-disc pl-6">
              <li>PEC: <a href="mailto:protocollo@pec.agid.gov.it" className="text-[#163321] underline">protocollo@pec.agid.gov.it</a></li>
              <li>Sito: <a href="https://www.agid.gov.it/it/design-servizi/accessibilita" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">agid.gov.it/accessibilita</a></li>
            </ul>

            <h2 className="text-lg font-heading text-[#163321] mt-6">Preparazione della dichiarazione</h2>
            <p>
              La presente dichiarazione è stata redatta in data <strong>22 aprile 2026</strong> sulla base di:
            </p>
            <ul className="list-disc pl-6">
              <li>Autovalutazione tecnica da parte del team di sviluppo</li>
              <li>Verifica manuale di conformità WCAG 2.1 AA</li>
              <li>Test con strumenti automatici (axe DevTools, Lighthouse)</li>
            </ul>
            <p className="text-xs">
              La dichiarazione verrà riesaminata almeno una volta l'anno o ad ogni modifica significativa del sito.
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}
